import React from "react";

const ShippingPolicy = () => {
  return (
    <div className="min-h-screen bg-white text-gray-800">
      <div className="bg-red-600 py-12 text-center text-white">
        <h1 className="text-3xl font-bold md:text-4xl">Shipping and Delivery Policy</h1>
        <p className="mt-3 text-sm md:text-base">For Customers Shopping on Havbit Marketplace</p>
        <div className="mt-4 text-xs md:text-sm opacity-90">
          <p>Last Updated: May 2026 | Effective Date: May 2026 | Version: 1.0</p>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-6 py-10 md:py-12">
        <div className="mb-10 rounded-lg border border-gray-200 bg-gray-50 p-6">
          <h2 className="mb-4 text-xl font-semibold text-red-600">Table of Contents</h2>
          <ol className="list-decimal list-inside space-y-2 text-sm md:text-base">
            <li>How Your Order Gets to You</li>
            <li>Logistics Partners</li>
            <li>Delivery Coverage Across India</li>
            <li>Estimated Delivery Timelines by Zone</li>
            <li>Tracking Your Order</li>
            <li>Delivery Attempts and Missed Deliveries</li>
            <li>Shipping Charges</li>
            <li>Order Arrived Damaged</li>
            <li>Order Not Delivered — Next Steps</li>
            <li>Special Delivery Situations</li>
          </ol>
        </div>

        <div className="space-y-8 text-base leading-7 text-gray-800">
          <p>
            This Shipping and Delivery Policy explains in detail how orders placed on the Havbit
            marketplace are prepared, dispatched, tracked, and delivered to customers across India. It
            covers delivery coverage, timelines, tracking, delivery attempts, special delivery
            situations, and what to do when your order does not arrive as expected. All deliveries are
            coordinated by Havbit through its network of logistics partners.
          </p>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">1. How Your Order Gets to You — The Delivery Journey</h2>
            <p>
              Understanding the journey your order takes from the seller to your doorstep will help you track it effectively and know what to expect at each stage.
            </p>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full border border-gray-200 text-left text-sm">
                <thead className="bg-red-50 text-gray-900">
                  <tr>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">Stage</th>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">What Happens</th>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">Who Is Responsible</th>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">Timeline</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Order Confirmed</td>
                    <td className="border border-gray-200 px-3 py-2">You place the order and payment is confirmed. The seller receives the order notification.</td>
                    <td className="border border-gray-200 px-3 py-2">Havbit platform</td>
                    <td className="border border-gray-200 px-3 py-2">Immediate</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Order Processing</td>
                    <td className="border border-gray-200 px-3 py-2">The seller picks, quality checks, and packs your order, then marks it ready for pickup.</td>
                    <td className="border border-gray-200 px-3 py-2">Seller</td>
                    <td className="border border-gray-200 px-3 py-2">Within 24 hours</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Courier Pickup</td>
                    <td className="border border-gray-200 px-3 py-2">Havbit's logistics partner collects the shipment from the seller's premises and generates a tracking number.</td>
                    <td className="border border-gray-200 px-3 py-2">Havbit logistics</td>
                    <td className="border border-gray-200 px-3 py-2">Within 24–48 hours of dispatch SLA</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">In Transit</td>
                    <td className="border border-gray-200 px-3 py-2">The shipment travels through the courier's network to the delivery hub closest to your pin code.</td>
                    <td className="border border-gray-200 px-3 py-2">Courier partner</td>
                    <td className="border border-gray-200 px-3 py-2">Varies by zone</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Out for Delivery</td>
                    <td className="border border-gray-200 px-3 py-2">The shipment is loaded on a delivery vehicle in your area and a delivery attempt is made.</td>
                    <td className="border border-gray-200 px-3 py-2">Courier partner</td>
                    <td className="border border-gray-200 px-3 py-2">Day of delivery</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Delivered</td>
                    <td className="border border-gray-200 px-3 py-2">The shipment is handed over to you or an authorised person at your delivery address.</td>
                    <td className="border border-gray-200 px-3 py-2">Courier partner</td>
                    <td className="border border-gray-200 px-3 py-2">As per timeline</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">2. Logistics Partners</h2>
            <p>
              Havbit works with a network of leading logistics and courier service providers to deliver your orders across India. Our current logistics partners include:
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li>Delhivery: One of India's largest logistics companies, providing extensive coverage across metro cities, tier-2 and tier-3 cities, and remote areas</li>
              <li>XpressBees: A fast-growing courier network with strong coverage in western and southern India, known for speed in metro deliveries</li>
              <li>Ekart Logistics: A reliable logistics partner with deep penetration into smaller towns and semi-urban areas</li>
            </ul>
            <p className="mt-4">
              Havbit's platform automatically assigns the best available courier partner for your specific delivery pin code, based on serviceability, estimated delivery time, and current capacity. You do not need to choose a courier — this is handled automatically. The assigned courier partner for your order will be shown on your order tracking page.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">3. Delivery Coverage Across India</h2>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">3.1 PAN India Delivery</h3>
            <p>
              Havbit provides delivery coverage across India — from major metropolitan cities to smaller towns, district headquarters, and semi-urban areas. Delivery availability at your specific pin code depends on the serviceability of our courier partner network at that pin code. You can check delivery availability by entering your pin code on any product page or at the checkout stage.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">3.2 Areas with Limited or No Coverage</h3>
            <p>
              While Havbit strives for maximum coverage, certain highly remote areas — including some locations in the Himalayan regions, certain islands, and interior tribal areas — may not be serviceable by our current logistics network. If your pin code is not serviceable, you will be informed at the product page or checkout stage before you place your order, so you are never charged for a delivery that cannot be made.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">3.3 Temporary Serviceability Issues</h3>
            <p>
              In rare cases, a pin code that is usually serviceable may temporarily become unserviceable due to natural events, civil situations, courier capacity issues, or other factors. If your order is placed and the delivery is subsequently found to be unserviceable, you will be notified and a full refund will be processed.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">4. Estimated Delivery Timelines by Zone</h2>
            <p>
              Delivery timelines on Havbit are estimated based on the delivery zone — which is determined by the distance between the seller's dispatch location and your delivery address. The following are typical estimated delivery timelines. These are estimates and are not guaranteed commitments.
            </p>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full border border-gray-200 text-left text-sm">
                <thead className="bg-red-50 text-gray-900">
                  <tr>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">Zone</th>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">Destination</th>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">Typical Examples</th>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">Estimated Timeline</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Zone A — Local</td>
                    <td className="border border-gray-200 px-3 py-2">Same city or district</td>
                    <td className="border border-gray-200 px-3 py-2">Delhi to Delhi, Mumbai to Mumbai</td>
                    <td className="border border-gray-200 px-3 py-2">1 – 2 business days</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Zone B — Nearby</td>
                    <td className="border border-gray-200 px-3 py-2">Same state, different district</td>
                    <td className="border border-gray-200 px-3 py-2">Delhi to Gurgaon, Mumbai to Pune</td>
                    <td className="border border-gray-200 px-3 py-2">2 – 3 business days</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Zone C — Regional</td>
                    <td className="border border-gray-200 px-3 py-2">Neighbouring or nearby states</td>
                    <td className="border border-gray-200 px-3 py-2">Delhi to Jaipur, Mumbai to Ahmedabad</td>
                    <td className="border border-gray-200 px-3 py-2">3 – 4 business days</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Zone D — Mid-Range</td>
                    <td className="border border-gray-200 px-3 py-2">States within approx. 1,000 km</td>
                    <td className="border border-gray-200 px-3 py-2">Delhi to Kolkata, Mumbai to Hyderabad</td>
                    <td className="border border-gray-200 px-3 py-2">4 – 5 business days</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Zone E — Long Distance</td>
                    <td className="border border-gray-200 px-3 py-2">States beyond 1,000 km</td>
                    <td className="border border-gray-200 px-3 py-2">Delhi to Chennai, Mumbai to Assam</td>
                    <td className="border border-gray-200 px-3 py-2">5 – 6 business days</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Zone F — Remote / Special</td>
                    <td className="border border-gray-200 px-3 py-2">J&K, Ladakh, North-East, Andaman, Lakshadweep</td>
                    <td className="border border-gray-200 px-3 py-2">Itanagar, Kohima, Port Blair</td>
                    <td className="border border-gray-200 px-3 py-2">6 – 7 business days</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4">
              Important: Estimated timelines are calculated from the date of dispatch from the seller, not from the date you placed the order. If a seller takes up to 24 hours to dispatch, add 1 business day to the estimated delivery timeline. Delivery timelines do not include Sundays and national public holidays. During festive seasons (e.g. Diwali, Christmas, New Year), add 1–3 additional business days.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">5. Tracking Your Order</h2>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">5.1 Real-Time Order Tracking</h3>
            <p>
              Once your order is dispatched, you will receive a shipment tracking number via SMS and email. You can use this tracking number to monitor your delivery in real time. Tracking is available through the following channels:
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li>Havbit App: The most convenient tracking method. Open the app → “My Orders” → select your order → “Track Order”. The tracking map shows your package's real-time location and estimated delivery time</li>
              <li>Havbit Website: Log in to www.havbit.in → “My Orders” → select your order → “Track Order”</li>
              <li>Courier Partner Website / App: Visit the website or app of the assigned courier (Delhivery, XpressBees, or Ekart) and enter your tracking number for detailed tracking events</li>
              <li>SMS Notifications: Key delivery events — dispatch, out for delivery, and delivered — are communicated via SMS to your registered mobile number</li>
              <li>App Push Notifications: If you have enabled push notifications on the Havbit app, you will receive real-time delivery event alerts</li>
            </ul>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">5.2 Tracking Delays</h3>
            <p>
              Occasionally, there may be a delay between a physical delivery event and its reflection in the tracking system. If your tracking has not updated for more than 48 hours, please contact our customer support at hello@havbit.in and we will investigate with the courier partner.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">6. Delivery Attempts and What Happens If You Miss a Delivery</h2>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">6.1 Number of Delivery Attempts</h3>
            <p>
              Our courier partners will make up to 3 delivery attempts at your specified delivery address on separate business days before returning the shipment. Delivery attempts are typically made during standard business hours (8:00 AM to 8:00 PM). If the first attempt is unsuccessful, you will receive an SMS notification and the courier will attempt again on the next business day.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">6.2 Rescheduling a Delivery</h3>
            <p>
              If you know in advance that you will not be available during the delivery window, you may: contact the courier partner directly using the contact number provided in the delivery SMS to request a reschedule; arrange for a family member, neighbour, or colleague to accept the delivery on your behalf; or, for some courier partners, use the courier's app to select an alternative delivery slot.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">6.3 What Happens After All Attempts Fail</h3>
            <p>
              If all 3 delivery attempts are unsuccessful — because you were unavailable, the address was incorrect or inaccessible, or you could not be reached — the shipment will be returned to the seller. In this case:
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li>You will be notified that the shipment is being returned</li>
              <li>A refund will be processed after deduction of the forward and return shipping charges that Havbit incurred</li>
              <li>The seller will receive the product back and will not reship it unless you place a new order</li>
              <li>If the delivery failure was due to an error on Havbit's or the courier's part — such as an incorrect address on the shipping label despite your correct input — no deduction will be made and a full refund will be processed</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">7. Shipping Charges</h2>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">7.1 How Shipping Charges Are Calculated</h3>
            <p>
              Shipping charges for your order are calculated based on the total weight of your order and your delivery location (zone). The applicable shipping charge is displayed clearly at the checkout stage before you confirm your order — there are no hidden shipping charges.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">7.2 Free Shipping</h3>
            <p>
              Havbit offers free shipping on orders above a defined minimum order value, during promotional sale events, or for specific product categories. Free shipping thresholds and conditions are displayed on the platform and may vary from time to time. During checkout, if your order qualifies for free shipping, this will be shown before you confirm the order.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">7.3 Additional Charges for Remote Areas</h3>
            <p>
              Delivery to certain remote areas — including Zone F locations such as Jammu and Kashmir, Ladakh, North-East India, and island territories — may attract additional shipping surcharges due to higher logistics costs. Any such surcharge will be clearly displayed at checkout before you confirm your order.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">8. Order Arrived Damaged — What to Do</h2>
            <p>
              If your order arrives with visibly damaged outer packaging, we recommend the following steps to protect your interests:
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li>Before accepting delivery: If the outer packaging is visibly torn, crushed, wet, or tampered with, photograph or video the package before opening it. Accept the delivery but note the damage on the delivery receipt or in the courier's app</li>
              <li>After opening: Check the product condition immediately. If the product is damaged, raise a return request within 48 hours with photographs of both the outer packaging damage and the product damage</li>
              <li>If you refuse delivery: You may refuse to accept a severely damaged package. The courier will mark it as 'Customer Refused — Damaged' and return it to the seller. Contact Havbit at hello@havbit.in to arrange a replacement or refund</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">9. Order Not Delivered — Next Steps</h2>
            <p>
              If your order tracking shows 'Delivered' but you have not received the package, or if your order has not been delivered within 2 business days of the estimated delivery date, please take the following steps:
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li>First, check with family members, neighbours, building security, or reception area — the package may have been accepted by someone else on your behalf</li>
              <li>Check for a delivery notification card left by the courier with instructions for pickup or redelivery</li>
              <li>Contact the courier partner directly using the helpline number shown on your tracking page — they can provide GPS delivery location and delivery agent details</li>
              <li>If the above steps do not resolve the issue, email hello@havbit.in with your Order ID and a brief description. Our team will investigate with the courier and respond within 48 hours</li>
            </ul>
            <p className="mt-4">
              If an order is confirmed as lost in transit by the courier partner, Havbit will process a full refund to your original payment method within 5–7 business days, or dispatch a replacement order at your choice.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">10. Special Delivery Situations</h2>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">10.1 Delivery to Offices and Commercial Addresses</h3>
            <p>
              If you have provided an office or commercial address, please ensure someone is authorised to accept deliveries at that address during business hours. Some courier partners may require the recipient to show ID for delivery to commercial addresses.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">10.2 Delivery to Gated Communities and Residential Complexes</h3>
            <p>
              For deliveries to gated communities, apartments, and residential complexes, please ensure your complete flat number, wing/block, and building name are included in your delivery address. Inform your building security or reception that a delivery is expected if you will not be available.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">10.3 Multiple Item Orders</h3>
            <p>
              When you order multiple products from different sellers, they will be shipped separately by each seller. Each shipment will have its own tracking number. You may receive your items at different times — this is normal for marketplace orders and does not indicate a problem.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">10.4 Deliveries During Festivals and Peak Periods</h3>
            <p>
              During major festive periods — including Diwali, Holi, Eid, Christmas, and New Year — and during Havbit's sale events, order volumes increase significantly and delivery timelines may be extended by 1–3 additional business days. We recommend ordering in advance during these periods if you need delivery by a specific date.
            </p>
          </section>

          <div className="border-t border-gray-200 pt-8 text-sm text-gray-700">
            <p className="font-semibold text-gray-900">Havbit Global Private Limited</p>
            <p>CIN: U63120HR2026PTC141507</p>
            <p>Innov8 UCP, 9th Floor, Tower D, Unitech Cyber Park, Gurgaon – 122001, Haryana, India</p>
            <p>Customer Support Email: hello@havbit.in</p>
            <p>Website: www.havbit.in</p>
            <p>Support Hours: Monday – Saturday, 10:00 AM – 6:00 PM IST</p>
            <p className="mt-4">
              By using the Havbit platform — whether by browsing, creating an account, or placing an order — you confirm that you have read, understood, and agree to be bound by this Shipping and Delivery Policy and all related Havbit policies as updated from time to time.
            </p>
          </div>

          <div className="border-t border-gray-200 pt-6 text-center text-xs font-medium text-gray-600">
            © 2026 Havbit Global Private Limited | www.havbit.in | hello@havbit.in
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShippingPolicy;